import * as crypto from "crypto-js";
import { environment } from "src/environments/environment";
import { Appointment } from "../models/admissions.model";
import { normalizeEncounterAppointmentBillingFields } from "./encounter-claim-status.util";

const PHI_OPENSSL_PREFIX = "U2FsdGVkX1";

/** CryptoJS/OpenSSL ciphertext embedded in list-clinic `encountersPatient` fields. */
export function looksLikeEncryptedPhi(value: string | null | undefined): boolean {
  const s = String(value ?? "").trim();
  return s.length >= 24 && s.startsWith(PHI_OPENSSL_PREFIX);
}

/** Filter-key or other non-name strings that must not display in the patient column. */
export function isUsableEncounterPatientDisplayName(
  value: string | null | undefined
): boolean {
  const s = String(value ?? "").trim();
  if (!s || looksLikeEncryptedPhi(s)) {
    return false;
  }
  if (/^(id|ref|n|patient):/i.test(s)) {
    return false;
  }
  if (/^[^\p{L}\p{N}]{1,3}:\s*\p{L}/u.test(s)) {
    return false;
  }
  return true;
}

export function pickBetterEncounterPatientDisplayName(
  current: string | null | undefined,
  candidate: string | null | undefined
): string {
  const score = (value: string | null | undefined): number => {
    const text = formatPatientDisplayName(value);
    if (!isUsableEncounterPatientDisplayName(text)) {
      return -1;
    }
    return text.length;
  };
  const currentScore = score(current);
  const candidateScore = score(candidate);
  if (currentScore >= candidateScore) {
    return formatPatientDisplayName(current);
  }
  return formatPatientDisplayName(candidate);
}

/**
 * Trims, collapses whitespace, and converts ALL-CAPS names (common from EMR / PHI payloads)
 * to title case while leaving already mixed-case names unchanged.
 */
export function formatPatientDisplayName(
  value: string | null | undefined
): string {
  const s = String(value ?? "").trim().replace(/\s+/g, " ");
  if (!s) {
    return "";
  }
  const letters = s.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, "");
  if (letters.length > 0 && letters === letters.toUpperCase()) {
    return s
      .split(" ")
      .map((word) => {
        if (!word) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ");
  }
  return s;
}

const ENCOUNTER_PATIENT_NAME_FIELDS = [
  "name",
  "firstName",
  "first_name",
  "lastName",
  "last_name",
] as const;

export function formatEncounterPatientDisplayFields(
  ep: Record<string, unknown>
): void {
  for (const key of ENCOUNTER_PATIENT_NAME_FIELDS) {
    if (typeof ep[key] === "string") {
      ep[key] = formatPatientDisplayName(ep[key] as string);
    }
  }
  synthesizeEncounterPatientName(ep);
  if (typeof ep["name"] === "string") {
    ep["name"] = formatPatientDisplayName(ep["name"] as string);
  }
}

function decryptPhiString(value: string): string {
  const s = value.trim();
  if (!s || s.length < 24 || !s.startsWith(PHI_OPENSSL_PREFIX)) {
    return value;
  }
  try {
    const bytes = crypto.AES.decrypt(s, environment.signatureKey);
    const plain = bytes.toString(crypto.enc.Utf8);
    if (!plain || /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(plain)) {
      return value;
    }
    return plain;
  } catch {
    return value;
  }
}

function decryptPhiDeep(value: unknown): unknown {
  if (value == null) {
    return value;
  }
  if (typeof value === "string") {
    return decryptPhiString(value);
  }
  if (typeof value !== "object" || value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => decryptPhiDeep(entry));
  }
  const source = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    out[key] = decryptPhiDeep(source[key]);
  }
  return out;
}

export function synthesizeEncounterPatientName(ep: Record<string, unknown>): void {
  const existing = String(ep["name"] || "").trim();
  const first = String(ep["firstName"] || ep["first_name"] || "").trim();
  const last = String(ep["lastName"] || ep["last_name"] || "").trim();
  const combined = [first, last].filter(Boolean).join(" ").trim();
  if (combined && !isUsableEncounterPatientDisplayName(existing)) {
    ep["name"] = combined;
    return;
  }
  if (!existing && combined) {
    ep["name"] = combined;
  }
}

/** Decrypt nested `encountersPatient` / `encounters_patient` on a list-clinic row. */
export function normalizeEncounterPatientPhi<T extends Appointment>(
  appointment: T
): T {
  if (!appointment || typeof appointment !== "object") {
    return appointment;
  }
  const next = { ...appointment } as T & Record<string, unknown>;
  const record = next as Record<string, unknown>;
  const ep = next.encountersPatient;
  if (ep != null && typeof ep === "object") {
    const decrypted = decryptPhiDeep(ep) as Record<string, unknown>;
    formatEncounterPatientDisplayFields(decrypted);
    next.encountersPatient = decrypted as T["encountersPatient"];
  }
  const snake = record["encounters_patient"];
  if (snake != null && typeof snake === "object") {
    const decryptedSnake = decryptPhiDeep(snake) as Record<string, unknown>;
    formatEncounterPatientDisplayFields(decryptedSnake);
    record["encounters_patient"] = decryptedSnake;
    if (!next.encountersPatient) {
      next.encountersPatient = decryptedSnake as T["encountersPatient"];
    }
  }
  return normalizeEncounterAppointmentBillingFields(next);
}
